import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

/**
 * MiniSlider — stacked-layered crossfade (no AnimatePresence), zero blank gap.
 * All images stay mounted; only opacity animates.
 *
 * Usage: <MiniSlider images={[{src, alt}, ...]} interval={6500} />
 */
export const MiniSlider = ({ images, interval = 6500, alt = "", className = "" }) => {
  const [active, setActive] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (mq?.matches) setReducedMotion(true);
  }, []);

  // Preload all images so rotation is silky from the first cycle.
  useEffect(() => {
    images.forEach((it) => {
      const img = new Image();
      img.src = it.src;
    });
  }, [images]);

  useEffect(() => {
    if (reducedMotion || !images.length) return;
    const t = setInterval(() => setActive((i) => (i + 1) % images.length), interval);
    return () => clearInterval(t);
  }, [images.length, interval, reducedMotion]);

  if (!images.length) return null;

  return (
    <div className={`relative w-full h-full overflow-hidden bg-[#F3EFE9] ${className}`}>
      {images.map((it, idx) => (
        <motion.img
          key={idx}
          src={it.src}
          alt={it.alt || alt}
          className="absolute inset-0 w-full h-full object-cover"
          initial={false}
          animate={{
            opacity: idx === active ? 1 : 0,
            scale: idx === active && !reducedMotion ? 1.08 : 1,
          }}
          transition={{
            opacity: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
            scale: { duration: 7, ease: "linear" },
          }}
          loading="eager"
          decoding="async"
          style={{ zIndex: idx === active ? 2 : 1 }}
        />
      ))}
    </div>
  );
};

export default MiniSlider;
